import type { Organization } from '../services/api';

export const ADMIN_HOME_PATH = '/admin';
export const ADMIN_COMPANIES_BROWSE_PATH = '/admin?tab=companies&browse=1';
export const ADMIN_SOWS_PATH = '/admin?tab=sows';
const ORG_ID_PREFIX = 'org_';

/** Set on navigation from `/admin/companies/:companyId` → `/admin/sows/:sowId` so breadcrumbs keep company context */
export interface AdminFromCompanyDetailState {
    adminCompanyDetail?: {
        companyId: string;
        companyLabel?: string;
    };
    fromCompanyDetail?: {
        companyId: string;
        companyLabel?: string;
        /** Agreement display name — shown as final crumb instead of generic "SOW details" */
        sowName?: string;
    };
    /** Set when navigating from `/admin/sows/:sowId` → pricing config detail */
    fromSowDetail?: {
        sowId: string;
        sowName?: string;
        companyId: string;
        companyLabel?: string;
    };
}

export interface AdminBreadcrumbItem {
    label: string;
    path: string;
    /** When set, intermediate links preserve admin credit navigation context */
    state?: AdminFromCompanyDetailState;
}

export interface BuildAdminBreadcrumbsArgs {
    pathnames: string[];
    portalHomePath?: string;
    organizationLabel: string;
    adminCompanyDetail: AdminFromCompanyDetailState['adminCompanyDetail'];
    fromCompanyDetail: AdminFromCompanyDetailState['fromCompanyDetail'];
    fromSowDetail: AdminFromCompanyDetailState['fromSowDetail'];
    getAdminCompanyLabel: (numericCompanyId: number) => string;
}

function parseNumericId(value: string | undefined): number | null {
    return value && /^\d+$/.test(value) ? Number(value) : null;
}

export function readAdminOrgId(pathnames: string[], adminIndex: number): string | null {
    const segment = adminIndex >= 0 ? pathnames[adminIndex + 1] : null;
    return segment?.startsWith(ORG_ID_PREFIX) ? segment : null;
}

export function isAdminDetailRoute(
    pathnames: string[],
    adminIndex: number,
    resource: string,
): boolean {
    return (
        adminIndex >= 0 &&
        pathnames[adminIndex + 1] === resource &&
        Boolean(pathnames[adminIndex + 2]) &&
        pathnames.length === adminIndex + 3
    );
}

export function getAdminCompanyBreadcrumbLabel(
    organizations: Organization[] | undefined,
    numericCompanyId: number,
): string {
    const candidate = (organizations ?? []).find(
        (org) => Number(org.metadata?.company_id) === numericCompanyId,
    );
    return (
        candidate?.display_name?.trim() || candidate?.name?.trim() || `Company ${numericCompanyId}`
    );
}

/** Detail crumb labels keyed by the parent path segment (e.g. `/admin/sows/:sowId`). */
const DETAIL_LABEL_BY_PARENT_SEGMENT = new Map<string, string>([
    ['sows', 'SOW details'],
    ['agreements', 'SOW details'],
    ['pricing-configs', 'Pricing configuration details'],
    ['users', 'User details'],
]);

function buildCompanyDetailLabel(
    pathnames: string[],
    companySegment: string,
    adminCompanyDetail: AdminFromCompanyDetailState['adminCompanyDetail'],
    getAdminCompanyLabel: (numericCompanyId: number) => string,
): string {
    const companySegmentId = parseNumericId(companySegment);
    if (pathnames[0] !== 'admin' || companySegmentId === null) return 'Company details';

    const navigatedCompanyLabel =
        adminCompanyDetail?.companyId === companySegment
            ? adminCompanyDetail.companyLabel?.trim()
            : undefined;
    return navigatedCompanyLabel || getAdminCompanyLabel(companySegmentId);
}

function buildDynamicBreadcrumb(
    pathnames: string[],
    index: number,
    name: string,
    currentPath: string,
    organizationLabel: string,
    adminCompanyDetail: AdminFromCompanyDetailState['adminCompanyDetail'],
    getAdminCompanyLabel: (numericCompanyId: number) => string,
): AdminBreadcrumbItem | null {
    const parentSegment = pathnames[index - 1] ?? '';
    const crumb = (label: string): AdminBreadcrumbItem => ({ label, path: currentPath });

    if (parentSegment === 'companies') {
        return crumb(
            buildCompanyDetailLabel(pathnames, name, adminCompanyDetail, getAdminCompanyLabel),
        );
    }

    const detailLabel = DETAIL_LABEL_BY_PARENT_SEGMENT.get(parentSegment);
    if (detailLabel) return crumb(detailLabel);

    // `/admin/:orgId/sso` and `/admin/companies/:companyId/sso`
    if (name === 'sso' && index >= 2) return crumb('SSO');

    return buildOrgScopedBreadcrumb(pathnames, index, name, currentPath, organizationLabel);
}

/** Crumbs under `/admin/:orgId` (org teams, org user detail) and the generic fallback. */
function buildOrgScopedBreadcrumb(
    pathnames: string[],
    index: number,
    name: string,
    currentPath: string,
    organizationLabel: string,
): AdminBreadcrumbItem | null {
    const parentSegment = pathnames[index - 1];

    if (parentSegment === 'admin' && pathnames[0] === 'admin') {
        return { label: organizationLabel, path: currentPath };
    }

    if (parentSegment === 'user' && pathnames[index - 3] === 'admin') {
        return { label: 'User details', path: currentPath };
    }

    if (name === 'user' && pathnames[index - 2] === 'admin') return null;

    return { label: name.charAt(0).toUpperCase() + name.slice(1), path: currentPath };
}

function buildBaseBreadcrumbs({
    pathnames,
    portalHomePath = ADMIN_HOME_PATH,
    organizationLabel,
    adminCompanyDetail,
    getAdminCompanyLabel,
}: Omit<BuildAdminBreadcrumbsArgs, 'fromCompanyDetail' | 'fromSowDetail'>): AdminBreadcrumbItem[] {
    const breadcrumbs: AdminBreadcrumbItem[] = [{ label: 'Home', path: portalHomePath }];
    if (pathnames.length === 0) return breadcrumbs;

    let currentPath = '';
    pathnames.forEach((name, index) => {
        currentPath += `/${name}`;

        switch (name) {
            case 'attention':
                breadcrumbs.push({ label: 'Identity attention center', path: currentPath });
                return;
            case 'users':
                breadcrumbs.push({ label: 'Users', path: currentPath });
                return;
            case 'companies':
                breadcrumbs.push({
                    label: 'Companies',
                    path: pathnames[0] === 'admin' ? ADMIN_COMPANIES_BROWSE_PATH : currentPath,
                });
                return;
            case 'agreements':
                breadcrumbs.push({ label: 'SOW', path: currentPath });
                return;
            case 'sows':
                breadcrumbs.push({
                    label: 'SOW',
                    path: pathnames[0] === 'admin' ? ADMIN_SOWS_PATH : currentPath,
                });
                return;
            case 'pricing-configs':
                breadcrumbs.push({
                    label: 'Pricing configurations',
                    path: ADMIN_HOME_PATH,
                });
                return;
            case 'admin':
                return;
            default: {
                const crumb = buildDynamicBreadcrumb(
                    pathnames,
                    index,
                    name,
                    currentPath,
                    organizationLabel,
                    adminCompanyDetail,
                    getAdminCompanyLabel,
                );
                if (crumb) breadcrumbs.push(crumb);
            }
        }
    });

    return breadcrumbs;
}

function applyAdminSowDetailBreadcrumbContext(
    breadcrumbs: AdminBreadcrumbItem[],
    pathname: string,
    fromCompanyDetail: AdminFromCompanyDetailState['fromCompanyDetail'],
    isAdminSowDetail: boolean,
    getAdminCompanyLabel: (numericCompanyId: number) => string,
): AdminBreadcrumbItem[] {
    if (!fromCompanyDetail?.companyId || !isAdminSowDetail) {
        return breadcrumbs;
    }

    const companyId = fromCompanyDetail.companyId;
    const numericCompanyId = parseNumericId(companyId);
    const companyTitle =
        fromCompanyDetail.companyLabel?.trim() ||
        (numericCompanyId !== null ? getAdminCompanyLabel(numericCompanyId) : companyId);
    const sowLabel =
        fromCompanyDetail.sowName?.trim() || breadcrumbs[breadcrumbs.length - 1]?.label || 'SOW';

    return [
        breadcrumbs[0],
        { label: 'Companies', path: ADMIN_COMPANIES_BROWSE_PATH },
        {
            label: companyTitle,
            path: `/admin/companies/${companyId}`,
        },
        { label: sowLabel, path: pathname },
    ];
}

function applyAdminPricingConfigBreadcrumbContext(
    breadcrumbs: AdminBreadcrumbItem[],
    pathname: string,
    fromSowDetail: AdminFromCompanyDetailState['fromSowDetail'],
    isAdminPricingConfigDetail: boolean,
    getAdminCompanyLabel: (numericCompanyId: number) => string,
): AdminBreadcrumbItem[] {
    if (!fromSowDetail?.sowId || !fromSowDetail.companyId || !isAdminPricingConfigDetail) {
        return breadcrumbs;
    }

    const numericCompanyId = parseNumericId(fromSowDetail.companyId);
    const companyTitle =
        fromSowDetail.companyLabel?.trim() ||
        (numericCompanyId !== null
            ? getAdminCompanyLabel(numericCompanyId)
            : fromSowDetail.companyId);
    const sowLabel = fromSowDetail.sowName?.trim() || 'SOW';
    const sowReturnState: AdminFromCompanyDetailState = {
        fromCompanyDetail: {
            companyId: fromSowDetail.companyId,
            companyLabel: fromSowDetail.companyLabel,
            sowName: fromSowDetail.sowName,
        },
    };

    return [
        breadcrumbs[0],
        { label: 'Companies', path: ADMIN_COMPANIES_BROWSE_PATH },
        {
            label: companyTitle,
            path: `/admin/companies/${fromSowDetail.companyId}`,
        },
        {
            label: sowLabel,
            path: `/admin/sows/${fromSowDetail.sowId}`,
            state: sowReturnState,
        },
        {
            label: breadcrumbs[breadcrumbs.length - 1]?.label ?? 'Pricing configuration details',
            path: pathname,
        },
    ];
}

export function buildAdminBreadcrumbs({
    pathnames,
    portalHomePath,
    organizationLabel,
    adminCompanyDetail,
    fromCompanyDetail,
    fromSowDetail,
    getAdminCompanyLabel,
}: BuildAdminBreadcrumbsArgs): AdminBreadcrumbItem[] {
    const adminIndex = pathnames.indexOf('admin');
    const isAdminSowDetail = isAdminDetailRoute(pathnames, adminIndex, 'sows');
    const isAdminPricingConfigDetail = isAdminDetailRoute(pathnames, adminIndex, 'pricing-configs');
    const pathname = `/${pathnames.join('/')}`;

    let breadcrumbs = buildBaseBreadcrumbs({
        pathnames,
        portalHomePath,
        organizationLabel,
        adminCompanyDetail,
        getAdminCompanyLabel,
    });
    breadcrumbs = applyAdminSowDetailBreadcrumbContext(
        breadcrumbs,
        pathname,
        fromCompanyDetail,
        isAdminSowDetail,
        getAdminCompanyLabel,
    );
    return applyAdminPricingConfigBreadcrumbContext(
        breadcrumbs,
        pathname,
        fromSowDetail,
        isAdminPricingConfigDetail,
        getAdminCompanyLabel,
    );
}

export function getParentAdminBreadcrumb(
    breadcrumbs: AdminBreadcrumbItem[],
): { path: string; state?: AdminFromCompanyDetailState } | null {
    if (breadcrumbs.length < 2) {
        return null;
    }

    const parent = breadcrumbs[breadcrumbs.length - 2];
    return { path: parent.path, state: parent.state };
}
